import {
    ReferenceTokenAttrs,
    ReferenceTokenType,
    extensionFromFileName,
    fileKindFromExtension
} from './reference-token';

/**
 * Host-facing payload contract for "/" search insertions and token clicks.
 *
 * The `mentionCard` NODE is gone — both insertion paths now render the unified
 * `referenceToken` (see `reference-token.ts`). This module survives as the shape
 * hosts already speak: `edit-task-modal` / `add-task-modal` build a
 * `MentionCardAttrs` from a search row and receive one back in their
 * `mentionCardCallbacks`. Keeping it means the unification didn't ripple into
 * two very large host components.
 *
 * The mappers below are the single translation point between that shape and the
 * token's attrs.
 */

export type MentionCardType = 'file' | 'task' | 'client';

export interface MentionCardAttrs {
    /** 'file' | 'task' | 'client'. Folders are `file` + `isFolder: true`. */
    mentionType: MentionCardType;
    name: string;
    breadcrumb: string;
    entityId: number;
    /** Source backend entityType (1=Task, 3=Contact, 4=File, 5=Company). */
    entityType: number;
    refKey: string;

    // File-specific (mentionType === 'file')
    fileId: string;
    driveId: string;
    siteId: string;
    folderId: string;
    extension: string;
    fileSize: number;
    webUrl: string;
    isFolder: boolean;

    // Task-specific
    taskId: number;

    // Client-specific
    contactId: number;
    companyId: number;

    /** Tooltip-only subtitle; overrides `breadcrumb` on hover when set. */
    tooltipBreadcrumb: string;

    /** Refers to an entity that no longer exists — renders muted. */
    isDeleted: boolean;
}

/** Search-row payload → unified token attrs. */
export function mentionAttrsToTokenAttrs(m: Partial<MentionCardAttrs>): Partial<ReferenceTokenAttrs> {
    const mentionType: MentionCardType = m.mentionType || 'file';
    const ext: string = m.extension || extensionFromFileName(m.name || '');
    let type: ReferenceTokenType = 'file';

    if (mentionType === 'task') {
        type = 'task';
    } else if (mentionType === 'client') {
        type = 'client';
    } else if (m.isFolder === true) {
        type = 'folder';
    }

    return {
        type,
        fileKind: type === 'file' ? fileKindFromExtension(ext) : null,
        itemId: m.fileId || '',
        driveId: m.driveId || '',
        name: m.name || '',
        url: m.webUrl || '',
        status: m.isDeleted === true ? 'deleted' : 'ready',
        siteId: m.siteId || '',
        folderId: m.folderId || '',
        breadcrumb: m.breadcrumb || '',
        tooltipBreadcrumb: m.tooltipBreadcrumb || '',
        fileSize: m.fileSize || 0,
        extension: ext,
        entityId: m.entityId || 0,
        entityType: m.entityType || 0,
        taskId: m.taskId || 0,
        contactId: m.contactId || 0,
        companyId: m.companyId || 0,
        refKey: m.refKey || ''
    };
}

/** Unified token attrs → the payload host click callbacks expect. */
export function tokenAttrsToMentionAttrs(t: ReferenceTokenAttrs): MentionCardAttrs {
    let mentionType: MentionCardType = 'file';

    if (t.type === 'task') {
        mentionType = 'task';
    } else if (t.type === 'client') {
        mentionType = 'client';
    }

    return {
        mentionType,
        name: t.name || '',
        breadcrumb: t.breadcrumb || '',
        entityId: t.entityId || 0,
        entityType: t.entityType || 0,
        refKey: t.refKey || '',
        fileId: t.itemId || '',
        driveId: t.driveId || '',
        siteId: t.siteId || '',
        folderId: t.folderId || '',
        extension: t.extension || '',
        fileSize: t.fileSize || 0,
        webUrl: t.url || '',
        isFolder: t.type === 'folder',
        taskId: t.taskId || 0,
        contactId: t.contactId || 0,
        companyId: t.companyId || 0,
        tooltipBreadcrumb: t.tooltipBreadcrumb || '',
        isDeleted: t.status === 'deleted'
    };
}
